import {useMemo, useState, useEffect, useCallback, useRef} from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import {calenderBold, clockIcon, arrowRightSm, planeTakeOff, closeIcon, closeLight} from '@img/index.js'
import {BOOKING_CONSTRAINTS} from '@/constants/booking'
import {tripNow, roundToNext5Min} from '@/utils/dateUtils'
import {Loader} from '@/customComponents/uiComponents/Loader.jsx'

// `readOnly` = the view page (/rides/:id/view): the date / flight / time fields render
// exactly as they do on edit, but inert — no picker, no chevron, no Add button, and
// none of the opacity-50 "blocked" dimming a policy lock applies (nothing is blocked
// here, there's simply nothing to change).
//
// `saving` = the CONFIRM pages only: a pickup the user just chose is being re-quoted and
// written to the trip (useConfirmTrip's isSavingDateTime). The edit page never passes it
// and is unaffected.
//
// BOTH fields lock while it's true — one request covers scheduled_at and a second pick
// mid-flight would race it — but only the one the user actually touched shows the loader
// (`savingField`). The flag can't say which half changed, so the card remembers.
export default function DateTimeCard({trip, parentScheduledAt, isAirportTrip, hideFlightField = false, isFromAirport = false, isToAirport = false, onRecommend, flightNumber = '', onFlightNumberChange, onFlightClear, flightAdded = false, onSmartTime, onDateTimeChange, onDateChange, onTimeChange, dateHint, setDateHint, dateHintText = 'Adjust your date/time if needed', dateTimeSectionRef, hidePulse = false, applySmartTimeRef, leadTimeError = null, scheduledAtPolicy = {canEdit: true, blockedReason: null}, flightPolicy = {canEdit: true, blockedReason: null}, pulseField = null, readOnly = false, saving = false, gateError = null, gateErrorRef = null}) {
    // Headings follow the trip direction — the card used to say "Select Arrival …"
    // whatever the trip was, which named the wrong end of a to-airport run and invented
    // a flight for a city-to-city one. The flags come from the page's live destinations
    // (useTriDirection in useEditTrip), so changing the route in this session re-labels
    // these immediately rather than following the trip's committed direction.
    const dateHeading = isFromAirport ? 'Select Arrival Date'
        : isToAirport ? 'Select Departure Date'
        : 'Select Date'
    const timeHeading = isFromAirport ? 'Select Arrival Time'
        : isToAirport ? 'Select Departure Time'
        : 'Select Time'
    // ── Allowed date range ──
    // A return booking can't be scheduled before its parent ride, so the parent's
    // pickup DATE becomes the minimum. parentScheduledAt is a naive APP_TIMEZONE
    // "Y-m-d H:i:s" string — take the date parts literally (wall clock, same frame
    // as tripNow) instead of letting new Date() reinterpret them in the browser TZ.
    const minDate = useMemo(() => {
        let min = tripNow()
        if (parentScheduledAt) {
            const [py, pm, pd] = String(parentScheduledAt).split(/[ T]/)[0].split('-').map(Number)
            const p = new Date(py, pm - 1, pd, 0, 0, 0, 0)
            if (p > min) min = p
        }
        min.setHours(0, 0, 0, 0)
        return min
    }, [parentScheduledAt])
    const maxDate = useMemo(() => {
        const m = tripNow()
        m.setDate(m.getDate() + BOOKING_CONSTRAINTS.MAX_BOOKING_ADVANCE_DAYS)
        m.setHours(0, 0, 0, 0)
        return m
    }, [])

    // ── Selected datetime (single source of truth) ──
    // `selected` always holds a concrete Date so the pickers' bounds work; the
    // dateSet/timeSet flags track whether the trip (or the user) actually chose
    // each part. Until a part is set its field shows a "Please set…" placeholder
    // and nothing is committed to the parent — so a schedule-less draft (e.g. a
    // fresh return booking on the confirm page) never fakes "now" as a choice.
    const [selected, setSelected] = useState(() => (
        trip?.scheduled_at ? new Date(trip.scheduled_at) : roundToNext5Min(tripNow())
    ))
    const [dateSet, setDateSet] = useState(() => !!trip?.scheduled_at)
    const [timeSet, setTimeSet] = useState(() => !!trip?.scheduled_at)

    // ── Accordion open state + outside-click close ──
    const [openField, setOpenField] = useState(null) // 'date' | 'time' | null
    // Which half the user last touched. `saving` is ONE flag for the whole card — both
    // fields feed the same scheduled_at, so one request covers them — but the loader
    // belongs on the field that was actually just changed; the other only locks.
    const [savingField, setSavingField] = useState(null) // 'date' | 'time' | null
    const containerRef = useRef(null)
    const setRefs = useCallback((node) => {
        containerRef.current = node
        if (dateTimeSectionRef) dateTimeSectionRef.current = node
    }, [dateTimeSectionRef])
    useEffect(() => {
        if (!openField) return
        const onDown = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpenField(null)
        }
        document.addEventListener('mousedown', onDown)
        return () => document.removeEventListener('mousedown', onDown)
    }, [openField])

    // ── Commit to parent (each part only once actually chosen) ──
    // The date commits on its own so date-scoped consumers (the flight lookup /
    // smart suggestion popup reads liveDatetime.date) work before a time exists.
    // time/hour/minute stay null until the user picks a time or accepts the
    // smart suggestion — so an unchosen time can never become scheduled_at.
    useEffect(() => {
        if (!onDateTimeChange || !selected || !dateSet) return
        const y = selected.getFullYear()
        const mo = String(selected.getMonth() + 1).padStart(2, '0')
        const d = String(selected.getDate()).padStart(2, '0')
        const hh = String(selected.getHours()).padStart(2, '0')
        const mi = String(selected.getMinutes()).padStart(2, '0')
        onDateTimeChange({
            date: `${y}-${mo}-${d}T00:00:00`,
            time: timeSet ? `${y}-${mo}-${d}T${hh}:${mi}:00` : null,
            hour: timeSet ? selected.getHours() : null,
            minute: timeSet ? selected.getMinutes() : null,
        })
    }, [selected, dateSet, timeSet, onDateTimeChange])

    // ── Time-list bounds (mirrors travelingTime: today can't pick past, cap at latest booking hour) ──
    const isToday = useMemo(() => {
        const s = new Date(selected); s.setHours(0, 0, 0, 0)
        const t = tripNow(); t.setHours(0, 0, 0, 0)
        return s.getTime() === t.getTime()
    }, [selected])
    const minTime = useMemo(() => {
        if (isToday) return tripNow()
        const d = new Date(selected); d.setHours(0, 0, 0, 0); return d
    }, [isToday, selected])
    const maxTime = useMemo(() => {
        const d = new Date(selected)
        d.setHours(BOOKING_CONSTRAINTS.LATEST_BOOKING_HOUR, BOOKING_CONSTRAINTS.LATEST_BOOKING_MINUTE, 0, 0)
        return d
    }, [selected])

    const handleDatePick = (date) => {
        if (!date) return
        const n = new Date(date)
        n.setHours(selected.getHours(), selected.getMinutes(), 0, 0)
        setSelected(n)
        setDateSet(true)
        setSavingField('date')
        onDateChange?.()
        setOpenField(null)
    }
    const handleTimePick = (date) => {
        if (!date) return
        // Merge the picked time onto the working date so an unset date part
        // isn't silently replaced by the picker's default day.
        const n = new Date(selected)
        n.setHours(date.getHours(), date.getMinutes(), 0, 0)
        setSelected(n)
        setTimeSet(true)
        setSavingField('time')
        onTimeChange?.()
        setOpenField(null)
    }

    // ── Smart time applies the time only (keeps user's chosen date) ──
    const applySmartTime = useCallback((recommendedTime) => {
        setSelected(prev => {
            const n = new Date(prev)
            n.setHours(recommendedTime.getHours(), recommendedTime.getMinutes(), 0, 0)
            return n
        })
        // Accepting the smart suggestion counts as choosing both parts.
        setDateSet(true)
        setTimeSet(true)
        // It only moves the TIME, so that is the field that reports the save.
        setSavingField('time')
    }, [])
    useEffect(() => {
        if (applySmartTimeRef) applySmartTimeRef.current = applySmartTime
    }, [applySmartTime, applySmartTimeRef])

    const dateFieldLabel = dateSet ? selected.toLocaleDateString('en-US', {weekday: 'long', month: 'short', day: 'numeric'}) : 'Please set the date'
    const timeFieldLabel = timeSet ? selected.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit', hour12: true}) : 'Please set the time'
    // Read-only wins over the policy lock: on the view page nothing is "blocked", so the
    // dimming + blocked-reason tooltip stay off. `pickerOpens` is the single gate on every
    // interactive bit (click handler, chevron, calendar / time panel).
    const dateTimeLocked = (!readOnly && !scheduledAtPolicy.canEdit) || saving
    const pickerOpens = !readOnly && !dateTimeLocked

    // No "close the open panel when saving starts" effect is needed: every path that can
    // start a save (handleDatePick / handleTimePick / applySmartTime) has already closed
    // the panel, and `pickerOpens` keeps it shut for the duration either way.
    const Chevron = ({open, busy = false}) => (
        busy
            ? <span className="shrink-0 flex items-center"><Loader color="#201e20"/></span>
            : <img src={arrowRightSm} alt="" className="w-4.5 h-4.5 shrink-0 transition-transform duration-300" style={{transform: `rotate(${open ? -90 : 90}deg)`}}/>
    )
    const fieldClass = `bg-white rounded-(--radius-xl) border border-(--text-primary) flex items-center justify-between gap-3 px-4 py-1 min-h-[74px] ${readOnly ? '' : 'cursor-pointer'}`

    return (
        <div className="flex flex-col gap-4 relative" ref={setRefs}>
            {/* Date heading — direction-aware, see dateHeading */}
            <div className="relative group">
                <h2 className="font-bold text-xl mb-3">{dateHeading}</h2>
                {dateHint && !hidePulse && !dateSet && !readOnly && (
                    <div className="absolute right-0 -top-13 w-max max-w-[280px] bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-20 after:content-[''] after:absolute after:w-0 after:h-0 after:border-l-24 after:border-l-transparent after:border-r-[9px] after:border-r-transparent after:border-t-20 after:border-t-[#3c393c] after:-bottom-4.75 after:right-[20%]">
                        <button type="button" onClick={() => setDateHint?.(false)} className="ml-auto mb-1 text-white bg-transparent border-0 cursor-pointer text-base block"><img alt="close" src={closeIcon} className="invert"/></button>
                        <p className="mb-0!">{dateHintText}</p>
                    </div>
                )}
                <div onClick={pickerOpens ? () => setOpenField(openField === 'date' ? null : 'date') : undefined}
                     className={`${fieldClass} ${dateTimeLocked ? 'opacity-50 cursor-not-allowed!' : ''} ${(dateHint && !hidePulse && !dateSet) || pulseField === 'date' ? 'hopn-pulse-active' : ''}`}>
                    <span className="flex items-center gap-3 min-w-0">
                        <img src={calenderBold} alt="" className="w-5 h-5 shrink-0"/>
                        <span className={`text-base truncate ${dateSet ? '' : 'text-(--text-secondary)'}`}>{dateFieldLabel}</span>
                    </span>
                    {!readOnly && <Chevron open={openField === 'date'} busy={saving && savingField === 'date'}/>}
                </div>
                {pickerOpens && openField === 'date' && (
                    <div className="hopn-date-header hopn-edit-calendar absolute left-0 right-0 top-full z-[15] mt-2">
                        <DatePicker
                            inline
                            selected={dateSet ? selected : null}
                            onChange={handleDatePick}
                            minDate={minDate}
                            maxDate={maxDate}
                            showPopperArrow={false}
                            renderCustomHeader={({monthDate, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled}) => (
                                <div className="flex items-center justify-between px-2 mb-4">
                                    <span className="font-bold text-lg flex items-center gap-2">
                                        {monthDate.toLocaleDateString('en-US', {month: 'short', year: 'numeric'})}
                                        <img src={arrowRightSm} alt="" className="w-3.5 h-3.5"/>
                                    </span>
                                    <span className="flex items-center gap-6">
                                        <button type="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled} className="bg-transparent border-0 p-0 cursor-pointer disabled:opacity-30 flex">
                                            <img src={arrowRightSm} alt="prev" className="w-4 h-4" style={{transform: 'rotate(180deg)'}}/>
                                        </button>
                                        <button type="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled} className="bg-transparent border-0 p-0 cursor-pointer disabled:opacity-30 flex">
                                            <img src={arrowRightSm} alt="next" className="w-4 h-4"/>
                                        </button>
                                    </span>
                                </div>
                            )}
                        />
                    </div>
                )}
                {dateTimeLocked && scheduledAtPolicy.blockedReason && (
                    <div className="hidden group-hover:block absolute top-full right-0 mt-3 bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-10 w-max max-w-[280px] after:content-[''] after:absolute after:w-0 after:h-0 after:border-l-24 after:border-l-transparent after:border-r-[9px] after:border-r-transparent after:border-b-20 after:border-b-[#3c393c] after:-top-4.75 after:right-7.5">
                        <p className="mb-0!">{scheduledAtPolicy.blockedReason}</p>
                    </div>
                )}
                {gateError?.field === 'date' && (
                    <p ref={gateErrorRef} className="md:hidden text-red-500 text-sm font-semibold mt-2 mb-0! px-1">{gateError.message}</p>
                )}
            </div>

            {/* Enter Flight Number (airport trips) — type the number here, Add opens the Smart Time Select modal */}
            {isAirportTrip && !hideFlightField && (
                <div className="relative group/flight">
                    <h2 className="font-bold text-xl mb-3">Enter Flight Number</h2>
                    <div className="relative">
                    {/* Gate nudge — same bubble style as the date field; sits just above the input
                        (arrow pointing down) when the flight is the field blocking submit. */}
                    {pulseField === 'flight' && !readOnly && (
                        <div className="absolute right-0 bottom-full mb-2 w-max max-w-[280px] bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-20 after:content-[''] after:absolute after:w-0 after:h-0 after:border-l-24 after:border-l-transparent after:border-r-[9px] after:border-r-transparent after:border-t-20 after:border-t-[#3c393c] after:-bottom-4.75 after:right-[20%]">
                            <p className="mb-0!">Please add your flight number</p>
                        </div>
                    )}
                    <form onSubmit={(ev) => { ev.preventDefault(); if (!readOnly && flightNumber?.trim() && dateSet && flightPolicy.canEdit) onSmartTime?.(applySmartTime) }}
                          className={`bg-white rounded-(--radius-xl) border border-(--text-primary) flex items-center justify-between gap-3 pl-5 ${readOnly ? 'pr-5' : 'pr-2'} py-1 min-h-[74px] ${flightPolicy.canEdit || readOnly ? '' : 'opacity-50'} ${pulseField === 'flight' ? 'hopn-pulse-active' : ''}`}>
                        <span className="flex items-center gap-3 min-w-0 flex-1">
                            <img src={planeTakeOff} alt="" className="w-6 shrink-0 opacity-70"/>
                            {readOnly ? (
                                <span className={`flex-1 min-w-0 truncate text-base ${flightNumber ? '' : 'text-(--text-secondary)'}`}>{flightNumber || 'No flight number'}</span>
                            ) : (
                                <input
                                    type="text"
                                    value={flightNumber}
                                    placeholder="E.g. Ac4567"
                                    disabled={!flightPolicy.canEdit}
                                    onChange={(ev) => onFlightNumberChange?.(ev.target.value)}
                                    className="flex-1 min-w-0 bg-transparent outline-none text-base placeholder:text-(--text-secondary) disabled:cursor-not-allowed"
                                />
                            )}
                        </span>
                        {/* Flight lookup is date-scoped, so the arrival date must be picked first. */}
                        {!readOnly && (
                            <span className="relative group shrink-0">
                                {flightAdded ? (
                                    /* Flight is VALIDATED — the pill flips from "Add" to a clear (✕) so the
                                       passenger can both see it landed and undo it. Same icon + sizing the
                                       Smart Time modal already uses for this exact state, so the inline card
                                       and the modal read identically. type="button" so it can never
                                       re-trigger the lookup form; clearing wipes the flight from state
                                       entirely (see the hook's clearFlight). */
                                    <button type="button" onClick={() => onFlightClear?.()} disabled={!flightPolicy.canEdit}
                                            aria-label="Remove flight number"
                                            className="w-[2.625rem] h-[2.625rem] bg-(--accent-green-light) rounded-full border-0 cursor-pointer flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                                        <img src={closeLight} alt="Remove" className="w-4"/>
                                    </button>
                                ) : (
                                    <>
                                        <button type="submit" disabled={!flightNumber?.trim() || !dateSet || !flightPolicy.canEdit}
                                                className="w-[2.625rem] h-[2.625rem] bg-(--accent-green-light) rounded-full border-0 cursor-pointer text-(--text-primary) flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                                            Add
                                        </button>
                                        {flightPolicy.canEdit && !dateSet && (
                                            <span className="hidden group-hover:block absolute top-full right-0 mt-2 bg-[#3c393c] text-white rounded-lg px-3 py-2 text-xs text-center w-max max-w-[220px] z-30 after:content-[''] after:absolute after:bottom-full after:right-5 after:border-[6px] after:border-transparent after:border-b-[#3c393c]">Please add the date first</span>
                                        )}
                                    </>
                                )}
                            </span>
                        )}
                    </form>
                    </div>
                    {!readOnly && !flightPolicy.canEdit && flightPolicy.blockedReason && (
                        <div className="hidden group-hover/flight:block absolute top-full right-0 mt-2 bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-10 w-max max-w-[280px] after:content-[''] after:absolute after:w-0 after:h-0 after:border-l-24 after:border-l-transparent after:border-r-[9px] after:border-r-transparent after:border-b-20 after:border-b-[#3c393c] after:-top-4.75 after:right-7.5">
                            <p className="mb-0!">{flightPolicy.blockedReason}</p>
                        </div>
                    )}
                    {gateError?.field === 'flight' && (
                        <p ref={gateErrorRef} className="md:hidden text-red-500 text-sm font-semibold mt-2 mb-0! px-1">{gateError.message}</p>
                    )}
                </div>
            )}

            {/* Time heading — direction-aware, see timeHeading */}
            <div className="relative group">
                <h2 className="font-bold text-xl mb-3">{timeHeading}</h2>
                <div onClick={pickerOpens ? () => setOpenField(openField === 'time' ? null : 'time') : undefined} className={`${fieldClass} ${dateTimeLocked ? 'opacity-50 cursor-not-allowed!' : ''} ${pulseField === 'time' ? 'hopn-pulse-active' : ''}`}>
                    <span className="flex items-center gap-3 min-w-0">
                        <img src={clockIcon} alt="" className="w-5 h-5 shrink-0"/>
                        <span className={`text-base truncate ${timeSet ? '' : 'text-(--text-secondary)'}`}>{timeFieldLabel}</span>
                    </span>
                    {!readOnly && <Chevron open={openField === 'time'} busy={saving && savingField === 'time'}/>}
                </div>
                {pickerOpens && openField === 'time' && (
                    <div className="hopn-edit-timepicker absolute left-0 right-0 top-full z-[15] mt-2 bg-white rounded-[16px] border border-(--border-color) shadow-[0_4px_32px_#00000029] p-6">
                        <DatePicker
                            inline
                            selected={timeSet ? selected : null}
                            onChange={handleTimePick}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={BOOKING_CONSTRAINTS.TIME_SLOT_INTERVAL_MINUTES}
                            timeCaption="Time"
                            dateFormat="h:mm aa"
                            minTime={minTime}
                            maxTime={maxTime}
                        />
                    </div>
                )}
                {dateTimeLocked && scheduledAtPolicy.blockedReason && (
                    <div className="hidden group-hover:block absolute top-full right-0 mt-3 bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-10 w-max max-w-[280px] after:content-[''] after:absolute after:w-0 after:h-0 after:border-l-24 after:border-l-transparent after:border-r-[9px] after:border-r-transparent after:border-b-20 after:border-b-[#3c393c] after:-top-4.75 after:right-7.5">
                        <p className="mb-0!">{scheduledAtPolicy.blockedReason}</p>
                    </div>
                )}
                {gateError?.field === 'time' && (
                    <p ref={gateErrorRef} className="md:hidden text-red-500 text-sm font-semibold mt-2 mb-0! px-1">{gateError.message}</p>
                )}
            </div>

            {/* toAirport: the smart suggestion, after the time — same card as the booking
                flow's Date & Time step (.hopn-sd-date-time scopes its styles). */}
            {isToAirport && onRecommend && !readOnly && scheduledAtPolicy.canEdit && (
                <div className="hopn-sd-date-time">
                    <div
                        className="hopn-sd-recommend-card"
                        onClick={onRecommend}
                        onKeyDown={(ev) => {
                            if (ev.key === 'Enter' || ev.key === ' ') {
                                ev.preventDefault()
                                onRecommend()
                            }
                        }}
                        role="button"
                        tabIndex={0}
                    >
                        <div className="hopn-sd-recommend-text">
                            <h4>Get a Recommended Pickup Time</h4>
                            <p>Based on your flight and live traffic.</p>
                        </div>
                        <span className="hopn-sd-recommend-arrow" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4.5 12h14M12.5 6l6 6-6 6" stroke="#201e20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </span>
                    </div>
                </div>
            )}

            {leadTimeError && (
                <div className="hopn-quick-booking-error">
                    {leadTimeError}
                </div>
            )}

        </div>
    )
}
